import { useEffect, useRef, useState, type FocusEvent } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useQuery } from "@tanstack/react-query";
import api from "../../api";
import type { SolutionItem } from "../../api/types.ts";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import styles from "./ProjectCarouselSection.module.css";

type ProjectSlide = {
  id: string;
  name: string;
  intervention?: string;
  imageSrc?: string;
  imageAlt: string;
  href?: string;
};

const SKELETON_CARD_COUNT = 3;
const AUTOPLAY_DELAY_MS = 5500;
const solutionUrlOverrides: Record<string, string> = {
  // Temporary fallback until the backend consistently returns websiteUrl.
  "691de1f34fc0cf8a7d4443c4":
    "/projects/habitat-mosaic-creation-boothby-wildland-england",
};

const getSolutionName = (solution: SolutionItem) =>
  (
    solution.vbcWebsiteDisplayName ??
    solution.name ??
    solution.solutionName ??
    ""
  ).trim();

const getSolutionIntervention = (solution: SolutionItem) => {
  const intervention =
    solution.intervention ??
    solution.interventionType ??
    solution.subtitle ??
    solution.headline;

  return intervention?.trim();
};

const buildEarthlyProjectUrl = (websiteUrl?: string | null) => {
  const normalizedWebsiteUrl = websiteUrl?.trim();

  if (!normalizedWebsiteUrl) {
    return undefined;
  }

  try {
    return new URL(normalizedWebsiteUrl, "https://earthly.org/").toString();
  } catch {
    return undefined;
  }
};

const buildImageUrl = (imageUrl?: string | null) => {
  if (!imageUrl) {
    return undefined;
  }

  if (/^https?:\/\//i.test(imageUrl)) {
    return imageUrl;
  }

  if (imageUrl.startsWith("/design/")) {
    return imageUrl;
  }

  try {
    return new URL(imageUrl, import.meta.env.VITE_BACKEND_BASE_URL).toString();
  } catch {
    return imageUrl;
  }
};

const mapSolutionsToSlides = (solutions: SolutionItem[]): ProjectSlide[] => {
  return solutions.reduce<ProjectSlide[]>((slides, solution, index) => {
    const name = getSolutionName(solution);
    if (!name) {
      return slides;
    }

    slides.push({
      id: solution.id ?? `${name}-${index}`,
      name,
      intervention: getSolutionIntervention(solution),
      imageAlt: `${name} project landscape`,
      imageSrc: buildImageUrl(solution.images?.default),
      href: buildEarthlyProjectUrl(
        solution.websiteUrl ??
          (solution.id ? solutionUrlOverrides[solution.id] : undefined),
      ),
    });

    return slides;
  }, []);
};

const preloadImage = (src: string) =>
  new Promise<void>((resolve) => {
    if (typeof Image === "undefined") {
      resolve();
      return;
    }

    const image = new Image();

    image.onload = () => {
      resolve();
    };

    image.onerror = () => {
      resolve();
    };

    image.src = src;

    if (image.complete) {
      resolve();
    }
  });

const getSlideAnnouncement = (
  slide: ProjectSlide,
  index: number,
  total: number,
) =>
  slide.intervention
    ? `Showing slide ${index + 1} of ${total}: ${slide.name}. ${slide.intervention}.`
    : `Showing slide ${index + 1} of ${total}: ${slide.name}.`;

function ArrowIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {direction === "left" ? (
        <path d="M14.5 5.5L8 12l6.5 6.5" />
      ) : (
        <path d="M9.5 5.5L16 12l-6.5 6.5" />
      )}
    </svg>
  );
}

function ProjectCarouselSection() {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isInitialSlidesReady, setIsInitialSlidesReady] = useState(false);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
  const [slideAnnouncement, setSlideAnnouncement] = useState("");
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldAnnounceSelectionRef = useRef(false);
  const projectSlidesRef = useRef<ProjectSlide[]>([]);

  const { data: solutions = [], isPending } = useQuery<SolutionItem[]>({
    queryKey: ["solutions"],
    queryFn: api.get.solutions,
    staleTime: 300_000,
    retry: false,
  });

  const projectSlides = mapSolutionsToSlides(solutions);
  const hasMultipleSlides = projectSlides.length > 1;
  const initialImageLoadKey = Array.from(
    new Set(
      projectSlides
        .slice(0, SKELETON_CARD_COUNT)
        .map((slide) => slide.imageSrc)
        .filter((src): src is string => Boolean(src)),
    ),
  ).join("|");
  const isCarouselLoading =
    isPending || (projectSlides.length > 0 && !isInitialSlidesReady);
  const shouldAutoplay =
    hasMultipleSlides &&
    !isCarouselLoading &&
    !isAutoplayPaused &&
    !prefersReducedMotion;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: hasMultipleSlides,
    align: "center",
    skipSnaps: false,
  });

  useEffect(() => {
    projectSlidesRef.current = projectSlides;
  }, [projectSlides]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      const selectedIndex = emblaApi.selectedScrollSnap();

      setCurrentSlideIndex(selectedIndex);

      if (!shouldAnnounceSelectionRef.current) {
        return;
      }

      const selectedSlide = projectSlidesRef.current[selectedIndex];
      if (selectedSlide) {
        setSlideAnnouncement(
          getSlideAnnouncement(
            selectedSlide,
            selectedIndex,
            projectSlidesRef.current.length,
          ),
        );
      }

      shouldAnnounceSelectionRef.current = false;
    };

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    setCurrentSlideIndex(0);
    setSlideAnnouncement("");
    shouldAnnounceSelectionRef.current = false;
  }, [projectSlides.length]);

  useEffect(() => {
    if (!shouldAutoplay || !emblaApi) {
      return;
    }

    const autoplayTimer = window.setInterval(() => {
      emblaApi.scrollNext();
    }, AUTOPLAY_DELAY_MS);

    return () => {
      window.clearInterval(autoplayTimer);
    };
  }, [emblaApi, shouldAutoplay]);

  useEffect(() => {
    if (isPending) {
      setIsInitialSlidesReady(false);
      return;
    }

    if (projectSlides.length === 0) {
      setIsInitialSlidesReady(true);
      return;
    }

    if (!initialImageLoadKey) {
      setIsInitialSlidesReady(true);
      return;
    }

    const imageSources = initialImageLoadKey.split("|");
    let isCancelled = false;
    setIsInitialSlidesReady(false);

    void Promise.all(imageSources.map((src) => preloadImage(src))).then(() => {
      if (!isCancelled) {
        setIsInitialSlidesReady(true);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [initialImageLoadKey, isPending, projectSlides.length]);

  const goToPreviousSlide = () => {
    shouldAnnounceSelectionRef.current = true;
    emblaApi?.scrollPrev();
  };

  const goToNextSlide = () => {
    shouldAnnounceSelectionRef.current = true;
    emblaApi?.scrollNext();
  };

  const goToSlide = (index: number) => {
    if (index === currentSlideIndex) {
      return;
    }

    shouldAnnounceSelectionRef.current = true;
    emblaApi?.scrollTo(index);
  };

  const handleFocusOut = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsAutoplayPaused(false);
  };

  if (isCarouselLoading) {
    return (
      <section
        className={styles.section}
        aria-label="Featured biodiversity projects"
      >
        <div className={styles.container}>
          <div
            className={styles.skeletonCarousel}
            data-testid="project-carousel-skeleton"
            aria-hidden="true"
          >
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
              <div key={index} className={styles.skeletonCard} />
            ))}
          </div>
          <p className={styles.screenReaderStatus} role="status">
            Loading featured biodiversity projects.
          </p>
        </div>
      </section>
    );
  }

  if (projectSlides.length === 0) {
    return (
      <section
        className={styles.section}
        aria-label="Featured biodiversity projects"
      >
        <div className={styles.container}>
          <p className={styles.emptyState}>No project data available yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={styles.section}
      aria-label="Featured biodiversity projects"
    >
      <div className={styles.container}>
        <div
          className={styles.carousel}
          role="region"
          aria-roledescription="carousel"
          aria-label="Featured biodiversity projects carousel"
          onMouseEnter={() => setIsAutoplayPaused(true)}
          onMouseLeave={() => setIsAutoplayPaused(false)}
          onFocusCapture={() => setIsAutoplayPaused(true)}
          onBlurCapture={handleFocusOut}
        >
          <p
            className={styles.screenReaderStatus}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {slideAnnouncement}
          </p>
          <div className={styles.viewport}>
            <div ref={emblaRef} className={styles.emblaViewport}>
              <ul className={styles.track}>
                {projectSlides.map((project) => (
                  <li key={project.id} className={styles.slide}>
                    {project.href ? (
                      <a
                        className={styles.cardLink}
                        href={project.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={
                          project.intervention
                            ? `${project.name} - ${project.intervention}`
                            : project.name
                        }
                      >
                        <article className={styles.card}>
                          {project.imageSrc ? (
                            <img
                              className={styles.image}
                              src={project.imageSrc}
                              alt={project.imageAlt}
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className={styles.imageFallback}
                              aria-hidden="true"
                            />
                          )}
                          <div className={styles.overlay} aria-hidden="true" />
                          {project.intervention ? (
                            <p className={styles.intervention}>
                              {project.intervention}
                            </p>
                          ) : null}
                          <p className={styles.projectName}>{project.name}</p>
                        </article>
                      </a>
                    ) : (
                      <article
                        className={`${styles.card} ${styles.cardStatic}`}
                        aria-label={
                          project.intervention
                            ? `${project.name} - ${project.intervention}`
                            : project.name
                        }
                      >
                        {project.imageSrc ? (
                          <img
                            className={styles.image}
                            src={project.imageSrc}
                            alt={project.imageAlt}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className={styles.imageFallback}
                            aria-hidden="true"
                          />
                        )}
                        <div className={styles.overlay} aria-hidden="true" />
                        {project.intervention ? (
                          <p className={styles.intervention}>
                            {project.intervention}
                          </p>
                        ) : null}
                        <p className={styles.projectName}>{project.name}</p>
                      </article>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {hasMultipleSlides ? (
              <button
                type="button"
                className={`${styles.controlButton} ${styles.controlPrevious}`}
                onClick={goToPreviousSlide}
                aria-label="Show previous project"
              >
                <ArrowIcon direction="left" />
              </button>
            ) : null}

            {hasMultipleSlides ? (
              <button
                type="button"
                className={`${styles.controlButton} ${styles.controlNext}`}
                onClick={goToNextSlide}
                aria-label="Show next project"
              >
                <ArrowIcon direction="right" />
              </button>
            ) : null}
          </div>

          {hasMultipleSlides ? (
            <div className={styles.controls}>
              <div
                className={styles.dots}
                aria-label="Project slide indicators"
              >
                {projectSlides.map((project, index) => (
                  <button
                    key={project.id}
                    type="button"
                    className={`${styles.dot} ${index === currentSlideIndex ? styles.dotActive : ""}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to ${project.name}`}
                    aria-pressed={index === currentSlideIndex}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default ProjectCarouselSection;
